'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { TIMING } from '../../lib/constants';

interface RecordButtonProps {
  isRecording: boolean;
  durationMs: number;
  onTap: () => void;
  disabled?: boolean;
  /**
   * Show the elapsed time under the button. The session's answer dock draws
   * the time inside its own status line instead, so it turns this off; the
   * 30-second screen-reader announcement stays either way.
   * @default true
   */
  showDuration?: boolean;
}

export function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function RecordButton({
  isRecording,
  durationMs,
  onTap,
  disabled,
  showDuration = true,
}: RecordButtonProps) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative flex items-center justify-center">
        <AnimatePresence>
          {isRecording && (
            <motion.div
              className="absolute inset-0 rounded-full border-2 border-recording-pulse"
              initial={{ scale: 1, opacity: 0.6 }}
              animate={{ scale: 1.6, opacity: 0 }}
              transition={{
                duration: TIMING.RECORD_PULSE / 1000,
                repeat: Infinity,
                ease: 'easeOut',
              }}
            />
          )}
        </AnimatePresence>

        {!isRecording && !disabled && (
          <motion.div
            className="absolute inset-0 rounded-full border border-accent/30"
            animate={{ scale: [1.0, 1.02, 1.0] }}
            transition={{
              duration: TIMING.IDLE_PULSE / 1000,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        )}

        <motion.button
          type="button"
          onClick={onTap}
          disabled={disabled}
          data-testid="record-button"
          className={`relative z-10 flex h-16 w-16 items-center justify-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
            isRecording
              ? 'bg-recording-pulse focus-visible:ring-recording-pulse'
              : 'bg-accent focus-visible:ring-accent'
          } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
          whileTap={disabled ? undefined : { scale: 0.95 }}
          aria-label={isRecording ? 'Stop recording' : 'Start recording'}
        >
          <AnimatePresence mode="wait" initial={false}>
            {isRecording ? (
              <motion.div
                key="stop"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ duration: 0.15 }}
                // A literal 3px corner: the member app's `rounded-sm` is 8px,
                // which turned this 20px square into a dot, so Stop read as Record.
                className="h-5 w-5 rounded-[3px] bg-white"
              />
            ) : (
              <motion.svg
                key="mic"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ duration: 0.15 }}
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" x2="12" y1="19" y2="22" />
              </motion.svg>
            )}
          </AnimatePresence>
        </motion.button>
      </div>

      <AnimatePresence>
        {isRecording && showDuration && (
          <motion.span
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="font-body text-sm tabular-nums text-recording-pulse"
            aria-live="off"
          >
            {formatDuration(durationMs)}
          </motion.span>
        )}
      </AnimatePresence>
      {isRecording && (
        <span className="sr-only" aria-live="polite" aria-atomic="true">
          {Math.floor(durationMs / 30000) > 0
            ? `Recording: ${formatDuration(durationMs)}`
            : ''}
        </span>
      )}
    </div>
  );
}
